import { ApiError } from './core/api-error';
import { putToStorage } from './core/storage-upload';
import { tenantHttp, type HttpClient } from './core/http-client';
import type { MediaFile, UploadPurpose, UploadTicket } from './models';

// Mirrors the backend rules so people get an answer before anything is sent. The API checks
// again, and reads the file's first bytes after upload.
export const UPLOAD_RULES: Record<UploadPurpose, { types: string[]; maxBytes: number }> = {
  'business-logo': { types: ['image/png', 'image/jpeg', 'image/webp'], maxBytes: 2 * 1024 * 1024 },
  'product-image': { types: ['image/png', 'image/jpeg', 'image/webp'], maxBytes: 5 * 1024 * 1024 },
};

export class MediaService {
  constructor(private readonly http: HttpClient) {}

  createUpload = (b: { purpose: UploadPurpose; contentType: string; sizeBytes: number }) =>
    this.http.post<UploadTicket>('/business/media/uploads', b);

  complete = (id: string) => this.http.post<MediaFile>(`/business/media/${id}/complete`, {});

  // Ask for a signed URL, send the file to storage, then let the API check it.
  upload = async (purpose: UploadPurpose, file: File): Promise<MediaFile> => {
    const rules = UPLOAD_RULES[purpose];
    if (!rules.types.includes(file.type))
      throw new ApiError(400, 'BAD_REQUEST', 'Use a PNG, JPEG or WebP image.');
    if (file.size > rules.maxBytes)
      throw new ApiError(
        413,
        'PAYLOAD_TOO_LARGE',
        `Images can be at most ${String(rules.maxBytes / 1024 / 1024)} MB.`,
      );
    const { mediaId, upload } = await this.createUpload({
      purpose,
      contentType: file.type,
      sizeBytes: file.size,
    });
    await putToStorage(upload, file);
    return this.complete(mediaId);
  };
}

export const mediaService = new MediaService(tenantHttp);
